import React from 'react';
import {
  formatRatio,
  formatPercent as formatPercentStrict,
  formatMultiple,
  formatAxisTick
} from '../../utils/formatFigure';

/**
 * Shared presentation primitives for the Counterparty Opinion.
 * Pure consumers of the existing API payload — display mapping only
 * (risk zone → Condition wording).
 */

/** Map backend risk zones / health to Condition: Sound | Watch | Strained | Distressed. */
export const conditionFromZone = (riskZone, overallFinancialHealth) => {
  const fromHealth = {
    Exceptional: 'Sound',
    'Very Strong': 'Sound',
    Strong: 'Sound',
    Stable: 'Watch',
    Moderate: 'Watch',
    Weak: 'Strained',
    Distressed: 'Distressed',
    Critical: 'Distressed'
  };
  if (overallFinancialHealth && fromHealth[overallFinancialHealth]) {
    const label = fromHealth[overallFinancialHealth];
    const tone = label === 'Sound' ? 'low' : label === 'Distressed' ? 'high' : 'medium';
    return { label, tone };
  }
  switch (riskZone) {
    case 'Safe Zone':
      return { label: 'Sound', tone: 'low' };
    case 'Grey Zone':
      return { label: 'Watch', tone: 'medium' };
    case 'Distress Zone':
      return { label: 'Distressed', tone: 'high' };
    default:
      return { label: 'N/A', tone: 'neutral' };
  }
};

/** @deprecated Prefer conditionFromZone — kept for older call sites. */
export const riskFromZone = (riskZone) => {
  const c = conditionFromZone(riskZone);
  const legacy =
    c.label === 'Sound' ? 'Low' : c.label === 'Distressed' ? 'High' : c.label === 'N/A' ? 'N/A' : 'Medium';
  return { label: legacy, tone: c.tone, condition: c.label };
};

export const toneFromOhlsonBand = (band) => {
  switch (band) {
    case 'Low':
      return 'low';
    case 'Moderate':
      return 'medium';
    case 'High':
      return 'high';
    default:
      return 'neutral';
  }
};

export const trendGlyph = (overall) => {
  switch (overall) {
    case 'Improving':
      return { glyph: '\u2197', tone: 'low' };
    case 'Deteriorating':
      return { glyph: '\u2198', tone: 'high' };
    case 'Stable':
      return { glyph: '\u2192', tone: 'neutral' };
    default:
      return { glyph: '', tone: 'neutral' };
  }
};

export const cadenceFromZone = (riskZone, aiAnalysis = null) => {
  if (aiAnalysis?.committeeDecision?.monitoringCadence) {
    return aiAnalysis.committeeDecision.monitoringCadence;
  }
  switch (riskZone) {
    case 'Safe Zone':
      return 'Semi-annual';
    case 'Grey Zone':
      return 'Quarterly';
    case 'Distress Zone':
      return 'Monthly';
    default:
      return 'Quarterly';
  }
};

export const latestDirection = (periods, key, higherIsBetter = true) => {
  if (!periods || periods.length < 2) return null;
  const latest = periods[periods.length - 1]?.[key];
  const prior = periods[periods.length - 2]?.[key];
  if (latest == null || prior == null) return null;
  if (latest === prior) return { glyph: '\u2192', tone: 'neutral', delta: 0 };
  const rising = latest > prior;
  const good = rising === higherIsBetter;
  return {
    glyph: rising ? '\u2197' : '\u2198',
    tone: good ? 'low' : 'medium',
    delta: latest - prior
  };
};

export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 'Not computable';
  }
  if (decimals === 1) return Number(value).toFixed(1);
  return formatRatio(value);
};

export const formatPercent = (value) => formatPercentStrict(value);

export { formatRatio, formatMultiple, formatAxisTick };

export const fiscalYearOf = (dateLike) => String(dateLike || '').slice(0, 4);

export const RiskDot = ({ tone }) => (
  <span className={`fr-dot fr-dot-${tone || 'neutral'}`} aria-hidden="true" />
);

export const Segmented = ({ options, value, onChange, ariaLabel, small }) => (
  <div className="fr-segmented" role="group" aria-label={ariaLabel}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={value === option.value}
        onClick={() => onChange(option.value)}
        style={small ? { padding: '0.22rem 0.6rem', fontSize: '0.75rem' } : undefined}
      >
        {option.label}
      </button>
    ))}
  </div>
);

export const Stat = ({ label, children, sub }) => (
  <div className="fr-stat">
    <span className="fr-label">{label}</span>
    {children}
    {sub ? <div className="fr-stat-sub">{sub}</div> : null}
  </div>
);

/** Split evidence "fact — consequence" for specimen-style emphasis. */
export const EvidenceLine = ({ text }) => {
  const raw = String(text || '');
  const parts = raw.split(/\s+[—–]\s+/);
  if (parts.length < 2) return <>{raw}</>;
  return (
    <>
      {parts[0]}
      {' — '}
      <strong className="fr-evidence-consequence">{parts.slice(1).join(' — ')}</strong>
    </>
  );
};
